import {Formik} from 'formik'
import React, {useEffect, useState} from 'react'
import {Form} from 'react-bootstrap'
import {useIntl} from 'react-intl'
import TextInput from '../../widgets/components/Input/TextInput'
import {InputWithPhone} from '../../widgets/components/Input/InputPhone'
import TextArea from '../../widgets/components/Input/TextArea'
import brandingConfig from '../../../../config/brandingConfig'
import SelectSearchInput from '../../widgets/components/Input/SelectSearchInput'
import {useGetAllDropdownData} from '../../../hooks/useGetAllDropdownData'
import {ShareholdersTypes} from '../../stakeholders/stakeholders-and-investors/core/_constants'
import Skeleton from 'react-loading-skeleton'
import {useAuth} from '../../auth'
import * as Yup from 'yup'
import parsePhoneNumberFromString, {isValidPhoneNumber} from 'libphonenumber-js'
import {SelectInput} from '../../widgets/components/Input/SelectInput'
import {teamSizeOptions} from '../../profile/core/_constants'
import {industryOptions} from '../../../core/_constants'
import {getEarlyOffer, getInvitees} from '../core/_requests'
import {useNavigate} from 'react-router-dom'
import TagManager from 'react-gtm-module'
import {GTMEvent} from '../../../hooks/gtmHelpers'
import earlyAccessBanner from '../../../../media/EarlyAccess/recordskeeper-ai-early-access-landing-page-banner.png'
import earlyAccessBannerDark from '../../../../media/EarlyAccess/recordskeeper-ai-early-access-landing-page-banner-dark.png'
import {EarlyInviteCard} from './EarlyInviteCard'
import {useThemeMode} from '../../../../_metronic/partials'
import {EarlyInvitees} from './EarlyInvitees'

const EarlyAccessForm = () => {
  const {formatMessage} = useIntl()
  const {mode} = useThemeMode()
  const {companyId, currentUser} = useAuth()
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()

  const {countryOptions, stateOptions, setCountryId, phoneCodesOptions, stateDataLoading} =
    useGetAllDropdownData(ShareholdersTypes.INDIVIDUAL)

  const validationSchema = Yup.object().shape({
    firstName: Yup.string()
      .min(3, formatMessage({id: 'Minimum 3 characters'}))
      .max(50, formatMessage({id: 'Maximum 50 characters'}))
      .required(formatMessage({id: 'First name is required'})),
    lastName: Yup.string()
      .max(50, formatMessage({id: 'Maximum 50 characters'}))
      .required(formatMessage({id: 'Last name is required'})),
    email: Yup.string()
      .email(formatMessage({id: 'Invalid email format'}))
      .max(50, formatMessage({id: 'Maximum 50 characters'}))
      .required(formatMessage({id: 'Email is required'})),
    phone: Yup.string()
      .nullable()
      .test('phone-number-valid', 'Invalid phone number', function (value, ctx: any) {
        if (!value || value === '') return true

        const selectedPhoneCode = ctx.parent.phoneCode
        const selectedCountryCode = phoneCodesOptions.find(
          (option: any) => option.dial_code === selectedPhoneCode
        )?.code

        if (phoneCodesOptions.length === 0 && ctx.originalValue) {
          return true // Invalid phone code
        }

        try {
          const phoneNumber = String(ctx.originalValue)
          const parsedNumber = parsePhoneNumberFromString(phoneNumber, selectedCountryCode)

          return isValidPhoneNumber(parsedNumber?.number as string)
        } catch (error) {
          return false
        }
      }),
    companyName: Yup.string().required('Company Name is required'),
    industry: Yup.string().required('Industry is required'),
    teamSize: Yup.number().required('Team Size is required'),
    title: Yup.string().required('Title is required'),
    usecase: Yup.string().required('Use case is required'),
    acceptTerms: Yup.bool(),
  })

  const initialValues = {
    firstName: '',
    lastName: '',
    email: currentUser?.email || '',
    phoneCode: currentUser?.countryDialingCode || '',
    phone: currentUser?.contact || '',
    companyName: '',
    industry: '',
    country: null,
    state: null,
    teamSize: null,
    title: '',
    usecase: '',
    companyId: null,
    acceptTerms: false,
  }

  const handleSubmit = async (values: any) => {
    setLoading(true)
    try {
      const rawPayload = {
        firstName: values?.firstName,
        lastName: values?.lastName,
        email: values?.email,
        phoneCode: values?.phoneCode,
        phoneNumber: values?.phone,
        companyName: values?.companyName,
        industry: values?.industry,
        countryId: values?.country?.value,
        stateId: values?.state?.value,
        teamSize: values?.teamSize,
        title: values?.title,
        usecase: values?.usecase,
        companyId,
      }
      const {
        data: {success},
      } = await getEarlyOffer(rawPayload)
      if (success) {
        TagManager.dataLayer({
          dataLayer: {
            event: GTMEvent.EarlyAdopterFormSubmitSuccessful,
            action: 'normal',
            metadata: {
              company_id: companyId,
              user_id: currentUser?.userId,
              email: currentUser?.email,
              user_hash: currentUser?.user_hash,
            },
          },
        })
        navigate('success')
      } else {
        TagManager.dataLayer({
          dataLayer: {
            event: GTMEvent.EarlyAdopterFormSubmitFailed,
            action: 'normal',
            metadata: {
              company_id: companyId,
              user_id: currentUser?.userId,
              email: currentUser?.email,
              user_hash: currentUser?.user_hash,
            },
          },
        })
      }
    } catch (err) {
      TagManager.dataLayer({
        dataLayer: {
          event: GTMEvent.EarlyAdopterFormSubmitFailed,
          action: 'normal',
          metadata: {
            company_id: companyId,
            user_id: currentUser?.userId,
            email: currentUser?.email,
            user_hash: currentUser?.user_hash,
            error: err,
          },
        },
      })
      console.log(err)
    } finally {
      setLoading(false)
    }
  }
  const [invitees, setInvitees] = useState<any[]>([])
  useEffect(() => {
    if (companyId) {
      getConfirmedInvitees()
    }
  }, [companyId]) // eslint-disable-line react-hooks/exhaustive-deps

  const getConfirmedInvitees = async () => {
    try {
      if (companyId) {
        const {
          data: {success, data},
        } = await getInvitees(companyId)
        if (success) {
          setInvitees(data)
        }
      }
    } catch (err) {
      console.log(err)
    } finally {
    }
  }
  return (
    <>
      <div className='px-6  mb-8'>
        <div className='row'>
          <div className='col-md-9 bg-white rounded-2 p-6'>
            {/* Header */}
            <div className='shadow-md'>
              {mode === 'dark' ? (
                <img
                  src={earlyAccessBannerDark}
                  alt='RecordsKeeper AI Early Access'
                  className='w-100'
                />
              ) : (
                <img
                  src={earlyAccessBanner}
                  alt='RecordsKeeper AI Early Access'
                  className='w-100'
                />
              )}
            </div>

            <div className='bg-white p-4 px-0 px-md-4'>
              <div className='w-100 container py-md-8 py-4 px-md-8'>
                <Formik
                  initialValues={initialValues}
                  validationSchema={validationSchema}
                  // onSubmit={(values, {resetForm, setSubmitting}) => {
                  //   onSubmit(values, setSubmitting, resetForm)
                  // }}
                  onSubmit={handleSubmit}
                  validateOnMount
                >
                  {(formik: any) => {
                    setCountryId(formik?.values?.country?.value)
                    console.log(formik, ' formik')
                    return (
                      <Form>
                        <div className='row'>
                          <h1 className='text-center mb-0'>
                            {formatMessage({
                              id: `Lifetime 50% Off – Exclusive Early Adopter Offer!`,
                            })}
                          </h1>
                          <h1 className='text-center mb-4'>
                            {formatMessage({id: `(Only for the First 100 Paid Users!)`})}
                          </h1>
                          <p className='text-center mb-0 font-size-12'>
                            {formatMessage({
                              id: `Please fill out the form below to claim your Lifetime 50% Off offer. Our team will`,
                            })}
                          </p>
                          <p className='text-center mb-8 font-size-12'>
                            {formatMessage({id: `review your details and get back to you soon.`})}
                          </p>
                          <div className='col-md-6 col-12'>
                            <TextInput
                              fieldType='text'
                              label={formatMessage({id: 'First Name'})}
                              fieldName='firstName'
                              formik={formik}
                              placeholder={formatMessage({id: 'Enter First Name'})}
                              toolTipText={formatMessage({
                                id: 'Enter your legal first name as it appears on official documents.',
                              })}
                              isTooltipForModal={true}
                              onBlur={formik.handleBlur}
                            />
                          </div>
                          <div className='col-md-6 col-12'>
                            <TextInput
                              fieldType='text'
                              label={formatMessage({id: 'Last Name'})}
                              fieldName='lastName'
                              formik={formik}
                              placeholder={formatMessage({id: 'Enter Last Name'})}
                              toolTipText={formatMessage({
                                id: 'Enter your legal last name as it appears on official documents.',
                              })}
                              isTooltipForModal={true}
                              onBlur={formik.handleBlur}
                            />
                          </div>
                          <div className='col-md-12 col-12'>
                            <TextInput
                              fieldType='email'
                              label={formatMessage({id: 'Email ID'})}
                              fieldName='email'
                              formik={formik}
                              placeholder={formatMessage({id: 'Enter Email'})}
                              toolTipText={formatMessage({
                                id: 'Enter your email address. This will be your primary login credential.',
                              })}
                              isStarRequired={true}
                              onBlur={formik.handleBlur}
                              isDisabled={true}
                            />
                          </div>
                          <div className='col-md-12 col-12'>
                            <InputWithPhone
                              fieldName='phone'
                              selectOptionFieldName='phoneCode'
                              label={formatMessage({id: 'Phone'})}
                              placeholder={formatMessage({id: 'Enter Phone Number'})}
                              placeholderSelect='Select Country Code'
                              formik={formik}
                              options={phoneCodesOptions}
                              toolTipText={formatMessage({
                                id: 'Enter your active mobile number for account verification and important notifications.',
                              })}
                              isStarRequired={true}
                              isTooltipForModal={true}
                              onBlur={formik.handleBlur}
                              disabled={true}
                            />
                          </div>
                          <div className='col-md-6 col-12'>
                            <TextInput
                              fieldType='text'
                              label={formatMessage({id: 'Company Name'})}
                              fieldName='companyName'
                              formik={formik}
                              placeholder={formatMessage({id: 'Enter Company Name'})}
                              toolTipText={formatMessage({
                                id: 'Enter the registered name of your organization or business entity.',
                              })}
                              isStarRequired={true}
                              onBlur={formik.handleBlur}
                            />
                          </div>
                          <div className='col-md-6 col-12'>
                            <SelectInput
                              label={formatMessage({id: 'Industry'})}
                              fieldName={'industry'}
                              placeholder={formatMessage({id: 'Select Your Industry'})}
                              formik={formik}
                              toolTipText={formatMessage({
                                id: 'Enter the primary industry sector your organization operates in.',
                              })}
                              options={industryOptions}
                              onBlur={formik.handleBlur}
                              isStarRequired={true}
                            />
                          </div>

                          <div className='col-md-6 col-12'>
                            <SelectInput
                              label={formatMessage({id: 'Company Size'})}
                              fieldName={'teamSize'}
                              formik={formik}
                              placeholder={formatMessage({id: 'Enter Company Size'})}
                              toolTipText={formatMessage({
                                id: 'Provide the total number of employees in your organization globally.',
                              })}
                              options={teamSizeOptions}
                              onBlur={formik.handleBlur}
                            />
                          </div>
                          <div className='col-md-6 col-12'>
                            <TextInput
                              fieldType='text'
                              label={formatMessage({id: 'Title'})}
                              fieldName='title'
                              formik={formik}
                              placeholder={formatMessage({id: 'Enter Title'})}
                              toolTipText={formatMessage({
                                id: 'Enter your current job role or designation within your organization.',
                              })}
                              isStarRequired={true}
                              onBlur={formik.handleBlur}
                            />
                          </div>
                          <div className='col-md-6 col-12'>
                            <SelectSearchInput
                              label={formatMessage({id: 'Country'})}
                              fieldName='country'
                              placeholder={formatMessage({id: 'Select Country'})}
                              formik={formik}
                              toolTipText={formatMessage({
                                id: 'Select the country where your organization is primarily headquartered.',
                              })}
                              options={countryOptions}
                              isStarRequired
                              onBlur={formik.handleBlur}
                            />
                          </div>
                          <div className='col-md-6 col-12'>
                            {stateDataLoading ? (
                              <div className='col-md-5 mb-10 mb-md-0'>
                                <div className='w-50'>
                                  <Skeleton
                                    height={20}
                                    baseColor='var(--kt-skeleton-bg-color)'
                                    highlightColor='var(--kt-skeleton-highlight-bg-color)'
                                  />
                                </div>
                                <div className='mt-2'>
                                  <Skeleton
                                    height={30}
                                    baseColor='var(--kt-skeleton-bg-color)'
                                    highlightColor='var(--kt-skeleton-highlight-bg-color)'
                                  />
                                </div>
                              </div>
                            ) : (
                              <SelectSearchInput
                                label={formatMessage({id: 'State'})}
                                fieldName='state'
                                placeholder={formatMessage({id: 'Select State'})}
                                formik={formik}
                                toolTipText={formatMessage({
                                  id: 'Select the state/province where your organization is primarily located.',
                                })}
                                options={stateOptions}
                                isStarRequired={true}
                                onBlur={formik.handleBlur}
                              />
                            )}
                          </div>
                          <div className='col-md-12 col-12'>
                            <TextArea
                              label={formatMessage({id: 'Usecases'})}
                              fieldName='usecase'
                              formik={formik}
                              placeholder={formatMessage({id: 'Enter Usecases'})}
                              toolTipText={formatMessage({
                                id: 'Briefly describe how you plan to use RecordsKeeper.AI in your organization`s operations or for yourself.',
                              })}
                              isStarRequired
                              isTooltipForModal={true}
                              onBlur={formik.handleBlur}
                            />
                          </div>
                          <div>
                            <TextInput
                              fieldName={'acceptTerms'}
                              formik={formik}
                              linkText1={formatMessage({id: 'Terms of offer'})}
                              linkText2={formatMessage({id: 'Privacy Policy'})}
                              url1='https://docs.google.com/document/d/1sDf1Ryb8uhfh1SS1nqcW_yNd4t6k9S9aasoMIDs7zMo'
                              url2={brandingConfig.privacyUrl}
                              customText={formatMessage({id: 'I Agree'})}
                              placeholder=''
                              fieldType={'checkbox'}
                              isCheckbox={true}
                              isDisabled={loading}
                              isTooltipNotRequired
                              onBlur={formik.handleBlur}
                            />
                          </div>
                          {/* <div className='col-md-3 col-12'>
                      <CustomButton
                        isSubmitting={formik.isSubmitting}
                        isValid={formik.isValid}
                        isAcceptTerms={formik.values.acceptTerms}
                        buttonText={formatMessage({id: 'Get Invitation'})}
                        loading={loading}
                        isAcceptTermsPresent={true}
                      />
                    </div> */}

                          <div className='d-md-flex justify-content-center align-items-center'>
                            <button
                              className='btn btn-secondry px-12 py-6 rounded-1 font-size-14 w-100 w-md-auto'
                              onClick={() => navigate('../../dashboard')}
                            >
                              <span className='indicator-label font-size-13 p-0'>
                                {formatMessage({id: 'Go Back'})}
                              </span>
                            </button>

                            <button
                              className='btn btn-primary px-12 py-6 rounded-1 font-size-14 w-100 w-md-auto'
                              disabled={!formik.isValid || loading || !formik.values.acceptTerms}
                              onClick={() => handleSubmit(formik.values)}
                            >
                              {!loading && (
                                <span className='indicator-label font-size-13 p-0'>
                                  {formatMessage({id: 'Get Invitation'})}
                                </span>
                              )}
                              {loading && (
                                <span className='indicator-label font-size-13 p-0'>
                                  {formatMessage({id: 'Please wait...'})}
                                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                                </span>
                              )}
                            </button>
                          </div>
                        </div>
                      </Form>
                    )
                  }}
                </Formik>
              </div>
            </div>
          </div>
          <div className='col-md-3 mt-4 mt-md-0 px-0 px-md-3'>
            <EarlyInviteCard />
            <EarlyInvitees invitees={invitees} />
          </div>
        </div>
      </div>
    </>
  )
}

export default EarlyAccessForm
